import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { UserService } from '../../../common/service/user.service';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { SearchbarComponent } from './searchbar/searchbar.component';
import { MenuComponent } from './menu/menu.component';
import { Customer } from '../../../common/model/customer/customer';
import { PanelModule } from 'primeng/panel';
import { MenuModule } from 'primeng/menu';
import { PlatformService } from '../../../common/service/platform.service';
import { Role } from '../../auth/role.enum';
import { NgClass } from '@angular/common';
import { CustomerService } from '../../../common/service/customer.service';
import { UserPreferenceService } from '../../../common/service/user-preference.service';
import { CustomerMenuItem } from '../../../common/model/customer/customer-menu-item';
import { Constants } from '../../../common/helper/Constants';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'gate-header',
  standalone: true,
  imports: [PanelModule, BreadcrumbComponent, SearchbarComponent, MenuComponent, MenuModule, NgClass, RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent{
  private readonly userService = inject(UserService);
  private readonly platformService = inject(PlatformService);
  private readonly customerService = inject(CustomerService);
  private readonly userPreferenceService = inject(UserPreferenceService);

  @Input() public customer: Customer | undefined;
  @Input() public currentCustomerName = '';
  @Input() public isLoading = false;
  @Input() public customersMenuItem: CustomerMenuItem[] = [];
  @Input() public preferredCustomerLoadingId: string | null = null;
  @Output() public customerSelected = new EventEmitter<string>();
  @Output() public preferredCustomerSelected = new EventEmitter<{ customerId: string; event: Event }>();

  user = computed(() => this.userService.currentUser());
  public canSwitchCustomer = computed(() => this.user().roles.includes(Role.Customer) && this.user().assignedCustomers.length > 0);
  heroVisible = signal(true);
  public desktopSidebarOpen = signal(true);
  public routes = Constants.routes;

  constructor() {
    this.heroVisible.set(this.userPreferenceService.showHeroSection());
    this.userPreferenceService.initialize().then(() => {
      this.heroVisible.set(this.userPreferenceService.showHeroSection());
    });
    this.platformService.heroToggleEvent.subscribe((value) => {
      this.heroVisible.set(value);
    });
    this.desktopSidebarOpen.set(this.platformService.getIsDesktopSidebarOpen());
    this.platformService.desktopSidebarToggleEvent.subscribe((value) => {
      this.desktopSidebarOpen.set(value);
    });
    this.customerService.customerLogoUpdated.subscribe((customerId) => {
      if (this.customer && (this.customer.id === customerId || !customerId)) {
        const cachedCustomer = this.customerService.getCachedCustomer(this.customer.id);
        if (cachedCustomer) {
          this.customer = { ...cachedCustomer };
        } else if (customerId === this.customer.id) {
          this.customer = { ...this.customer, logoPath: '' };
        }
      }
    });
  }

  expandSidebar() {
    this.platformService.setDesktopSidebarOpen(true);
  }

  public setPreferredCustomer(customerId: string, event: Event) {
    event.stopPropagation();
    if (this.preferredCustomerLoadingId || this.userService.getPreferredCustomerNumber() === customerId) {
      return;
    }

    this.preferredCustomerSelected.emit({ customerId, event });
  }

  public switchCustomer(customerId: string) {
    if (!this.user().roles.includes(Role.Customer)) {
      return;
    }

    if (!this.customer || this.customer.id !== customerId) {
      this.customerSelected.emit(customerId);
    }
  }

  public customerLogoUrl(): string | null {
    return this.customerService.resolveCustomerLogoUrl(this.customer?.logoPath);
  }
}
